import React, { useState, useEffect, useRef, useMemo } from 'react';

const SCENES = [
  {
    id: 'plain-white',
    label: 'Neutral hell',
    bg: '#f0f0f0',
    bannerPos: { top: '8%', left: '8%', right: '8%', bottom: '8%' },
    shadow: '0 8px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
  },
  {
    id: 'plain-dark',
    label: 'Neutral dunkel',
    bg: '#1a1a2e',
    bannerPos: { top: '8%', left: '8%', right: '8%', bottom: '8%' },
    shadow: '0 8px 50px rgba(0,0,0,0.6), 0 0 100px rgba(255,255,255,0.03)',
  },
];

// Resolve base path for static assets (works with homepage "." and /designer/ deployments)
const BASE_PATH = (process.env.PUBLIC_URL || '').replace(/\/$/, '');

const MOCKUP_SCENES = [
  {
    id: 'wall-outdoor',
    label: 'Außenwand',
    src: `${BASE_PATH}/mockups/wall-outdoor.svg`,
    // Placement area on the brick wall
    placement: { top: '34%', left: '18%', width: '64%', height: '38%' },
    perspective: { perspective: 800, rotateY: -1.5, rotateX: 0.5 },
    shadow: '0 6px 30px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.2)',
    overlay: 'linear-gradient(160deg, rgba(255,255,255,0.06) 0%, transparent 40%, rgba(0,0,0,0.08) 100%)',
  },
  {
    id: 'fence-garden',
    label: 'Gartenzaun',
    src: `${BASE_PATH}/mockups/fence-garden.svg`,
    // Placement on the fence
    placement: { top: '30%', left: '20%', width: '60%', height: '35%' },
    perspective: { perspective: 900, rotateY: 1, rotateX: -0.5 },
    shadow: '0 4px 20px rgba(0,0,0,0.3), 0 1px 6px rgba(0,0,0,0.15)',
    overlay: 'linear-gradient(150deg, rgba(255,255,255,0.08) 0%, transparent 35%, rgba(0,0,0,0.05) 100%)',
  },
  {
    id: 'room-indoor',
    label: 'Wohnzimmer',
    src: `${BASE_PATH}/mockups/room-indoor.svg`,
    // Placement on the wall above sofa
    placement: { top: '8%', left: '22%', width: '56%', height: '48%' },
    perspective: { perspective: 1000, rotateY: 0, rotateX: 0 },
    shadow: '0 4px 24px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)',
    overlay: 'linear-gradient(170deg, rgba(255,255,255,0.05) 0%, transparent 30%, rgba(0,0,0,0.03) 100%)',
  },
  {
    id: 'event-stage',
    label: 'Bühne/Event',
    src: `${BASE_PATH}/mockups/event-stage.svg`,
    // Placement on the stage backdrop
    placement: { top: '12%', left: '16%', width: '68%', height: '55%' },
    perspective: { perspective: 850, rotateY: -0.8, rotateX: 0.3 },
    shadow: '0 0 40px rgba(99,102,241,0.2), 0 8px 30px rgba(0,0,0,0.5)',
    overlay: 'linear-gradient(160deg, rgba(99,102,241,0.04) 0%, transparent 40%, rgba(236,72,153,0.03) 100%)',
  },
];

function FinishingOverlay({ widthCm, heightCm, finishing, hemPerSide, grommets, hohlsaum }) {
  const hp = hemPerSide || {};
  const hs = hohlsaum || {};
  const hasHem = Object.values(hp).some(v => v > 0);
  const hasHohlsaum = Object.values(hs).some(v => v > 0);
  const hasGrommets = grommets?.enabled;

  if (!hasHem && !hasHohlsaum && !hasGrommets) return null;

  // Calculate percentage-based positions for overlay elements
  const hemPct = {
    top: (hp.top || 0) / heightCm * 100,
    bottom: (hp.bottom || 0) / heightCm * 100,
    left: (hp.left || 0) / widthCm * 100,
    right: (hp.right || 0) / widthCm * 100,
  };

  const hsPct = {
    top: (hs.top || 0) / heightCm * 100,
    bottom: (hs.bottom || 0) / heightCm * 100,
    left: (hs.left || 0) / widthCm * 100,
    right: (hs.right || 0) / widthCm * 100,
  };

  // Generate grommet positions
  const grommetPositions = useMemo(() => {
    if (!hasGrommets) return [];
    const positions = [];
    const sides = grommets.sides || { top: true, bottom: true, left: true, right: true };
    const spacingCm = grommets.customSpacingCm || 50;
    const isCorners = grommets.mode === 'corners';
    const hemPS = grommets.hemPerSide || hemPerSide || {};

    if (isCorners) {
      // Corner grommets only
      const insetX = ((hemPS.left || 0) / widthCm * 100) / 2 || 2;
      const insetY = ((hemPS.top || 0) / heightCm * 100) / 2 || 2;
      const insetXR = ((hemPS.right || 0) / widthCm * 100) / 2 || 2;
      const insetYB = ((hemPS.bottom || 0) / heightCm * 100) / 2 || 2;
      positions.push({ x: insetX, y: insetY });
      positions.push({ x: 100 - insetXR, y: insetY });
      positions.push({ x: insetX, y: 100 - insetYB });
      positions.push({ x: 100 - insetXR, y: 100 - insetYB });
    } else {
      // Evenly spaced grommets along edges
      const addSideGrommets = (side) => {
        const hemSide = hemPS[side] || 0;
        if (side === 'top' || side === 'bottom') {
          const yPct = side === 'top'
            ? (hemSide / heightCm * 100) / 2
            : 100 - (hemSide / heightCm * 100) / 2;
          const count = Math.max(2, Math.floor(widthCm / spacingCm) + 1);
          const insetL = Math.max(1, (hemPS.left || 0) / widthCm * 100 / 2);
          const insetR = Math.max(1, (hemPS.right || 0) / widthCm * 100 / 2);
          for (let i = 0; i < count; i++) {
            const xPct = insetL + (100 - insetL - insetR) * i / (count - 1);
            positions.push({ x: xPct, y: yPct || (side === 'top' ? 2 : 98) });
          }
        } else {
          const xPct = side === 'left'
            ? (hemSide / widthCm * 100) / 2
            : 100 - (hemSide / widthCm * 100) / 2;
          const count = Math.max(2, Math.floor(heightCm / spacingCm) + 1);
          const insetT = Math.max(1, (hemPS.top || 0) / heightCm * 100 / 2);
          const insetB = Math.max(1, (hemPS.bottom || 0) / heightCm * 100 / 2);
          for (let i = 0; i < count; i++) {
            const yPct = insetT + (100 - insetT - insetB) * i / (count - 1);
            positions.push({ x: xPct || (side === 'left' ? 2 : 98), y: yPct });
          }
        }
      };

      if (sides.top) addSideGrommets('top');
      if (sides.bottom) addSideGrommets('bottom');
      if (sides.left) addSideGrommets('left');
      if (sides.right) addSideGrommets('right');
    }

    // Deduplicate corner grommets (within 2% tolerance)
    const unique = [];
    for (const pos of positions) {
      const isDupe = unique.some(u => Math.abs(u.x - pos.x) < 2 && Math.abs(u.y - pos.y) < 2);
      if (!isDupe) unique.push(pos);
    }
    return unique;
  }, [hasGrommets, grommets, hemPerSide, widthCm, heightCm]);

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 3 }}>
      {/* Hem (Saum) visualization — subtle fold lines along edges */}
      {hasHem && (
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          {hemPct.top > 0 && (
            <line x1="0%" y1={`${hemPct.top}%`} x2="100%" y2={`${hemPct.top}%`}
              stroke="rgba(255,255,255,0.4)" strokeWidth="1" strokeDasharray="6 3" />
          )}
          {hemPct.bottom > 0 && (
            <line x1="0%" y1={`${100 - hemPct.bottom}%`} x2="100%" y2={`${100 - hemPct.bottom}%`}
              stroke="rgba(255,255,255,0.4)" strokeWidth="1" strokeDasharray="6 3" />
          )}
          {hemPct.left > 0 && (
            <line x1={`${hemPct.left}%`} y1="0%" x2={`${hemPct.left}%`} y2="100%"
              stroke="rgba(255,255,255,0.4)" strokeWidth="1" strokeDasharray="6 3" />
          )}
          {hemPct.right > 0 && (
            <line x1={`${100 - hemPct.right}%`} y1="0%" x2={`${100 - hemPct.right}%`} y2="100%"
              stroke="rgba(255,255,255,0.4)" strokeWidth="1" strokeDasharray="6 3" />
          )}
        </svg>
      )}

      {/* Hohlsaum (tunnel hem) visualization */}
      {hasHohlsaum && (
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          {hsPct.top > 0 && (
            <rect x="0%" y="0%" width="100%" height={`${hsPct.top}%`}
              fill="rgba(124,58,237,0.12)" stroke="rgba(124,58,237,0.35)" strokeWidth="1" strokeDasharray="4 2" />
          )}
          {hsPct.bottom > 0 && (
            <rect x="0%" y={`${100 - hsPct.bottom}%`} width="100%" height={`${hsPct.bottom}%`}
              fill="rgba(124,58,237,0.12)" stroke="rgba(124,58,237,0.35)" strokeWidth="1" strokeDasharray="4 2" />
          )}
          {hsPct.left > 0 && (
            <rect x="0%" y="0%" width={`${hsPct.left}%`} height="100%"
              fill="rgba(124,58,237,0.12)" stroke="rgba(124,58,237,0.35)" strokeWidth="1" strokeDasharray="4 2" />
          )}
          {hsPct.right > 0 && (
            <rect x={`${100 - hsPct.right}%`} y="0%" width={`${hsPct.right}%`} height="100%"
              fill="rgba(124,58,237,0.12)" stroke="rgba(124,58,237,0.35)" strokeWidth="1" strokeDasharray="4 2" />
          )}
        </svg>
      )}

      {/* Grommets (Ösen) */}
      {hasGrommets && grommetPositions.map((pos, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${pos.x}%`,
            top: `${pos.y}%`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          {/* Metallic grommet ring */}
          <svg width="14" height="14" viewBox="0 0 14 14">
            <circle cx="7" cy="7" r="6" fill="none" stroke="rgba(180,180,180,0.9)" strokeWidth="2" />
            <circle cx="7" cy="7" r="4" fill="rgba(0,0,0,0.3)" />
            <circle cx="7" cy="7" r="6" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
            {/* Metallic shine */}
            <path d="M4 3.5 Q7 2 10 3.5" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.5" />
          </svg>
        </div>
      ))}
    </div>
  );
}

function MockupView({ imageUrl, scene, widthCm, heightCm, finishing, hemPerSide, grommets, hohlsaum }) {
  const aspectRatio = widthCm / heightCm;
  const { perspective, rotateY, rotateX } = scene.perspective;

  return (
    <div className="relative w-full h-full">
      {/* Mockup background image */}
      <img
        src={scene.src}
        alt={scene.label}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ userSelect: 'none', pointerEvents: 'none' }}
      />
      {/* Design placement area */}
      {imageUrl && (
        <div
          className="absolute flex items-center justify-center"
          style={{
            top: scene.placement.top,
            left: scene.placement.left,
            width: scene.placement.width,
            height: scene.placement.height,
          }}
        >
          <div
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              aspectRatio: `${aspectRatio}`,
              position: 'relative',
              lineHeight: 0,
              transform: `perspective(${perspective}px) rotateY(${rotateY}deg) rotateX(${rotateX}deg)`,
              boxShadow: scene.shadow,
              transition: 'transform 0.4s ease',
            }}
          >
            <img
              src={imageUrl}
              alt="Design Vorschau"
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
            {/* Finishing overlay */}
            <FinishingOverlay
              widthCm={widthCm}
              heightCm={heightCm}
              finishing={finishing}
              hemPerSide={hemPerSide}
              grommets={grommets}
              hohlsaum={hohlsaum}
            />
            {/* Realistic light/shadow overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: scene.overlay,
                borderRadius: 1,
                zIndex: 4,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewOverlay({ canvasManager, widthCm, heightCm, finishing, hemPerSide, grommets, hohlsaum, bleedCm, onClose }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [activeScene, setActiveScene] = useState(0);
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'mockup'
  const [activeMockup, setActiveMockup] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!canvasManager) return;
    try {
      const url = canvasManager.toDataURL({ format: 'png', quality: 1 });
      setImageUrl(url);
    } catch (err) {
      console.error('Preview export failed:', err);
    }
  }, [canvasManager]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (viewMode === 'preview') {
        if (e.key === 'ArrowRight') setActiveScene((p) => Math.min(p + 1, SCENES.length - 1));
        if (e.key === 'ArrowLeft') setActiveScene((p) => Math.max(p - 1, 0));
      } else {
        if (e.key === 'ArrowRight') setActiveMockup((p) => Math.min(p + 1, MOCKUP_SCENES.length - 1));
        if (e.key === 'ArrowLeft') setActiveMockup((p) => Math.max(p - 1, 0));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, viewMode]);

  // Build finishing summary for the info badge
  const finishingSummary = useMemo(() => {
    const parts = [];
    const hp = hemPerSide || {};
    const hs = hohlsaum || {};
    const hemSides = ['top', 'bottom', 'left', 'right'].filter(s => (hp[s] || 0) > 0);
    const hsSides = ['top', 'bottom', 'left', 'right'].filter(s => (hs[s] || 0) > 0);
    if (hemSides.length > 0) parts.push(`Saum: ${hemSides.map(s => hp[s]).join('/')} cm`);
    if (hsSides.length > 0) parts.push(`Hohlsaum: ${hsSides.map(s => hs[s]).join('/')} cm`);
    if (grommets?.enabled) parts.push('Ösen');
    return parts.join(' · ');
  }, [hemPerSide, hohlsaum, grommets]);

  const scene = SCENES[activeScene];
  const mockupScene = MOCKUP_SCENES[activeMockup];
  const aspectRatio = widthCm / heightCm;

  return (
    <div className="fixed inset-0 z-[900] flex flex-col" style={{ background: '#111' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 bg-black/80 backdrop-blur-sm z-10">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-white/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/>
          </svg>
          <span className="text-white/90 text-sm font-semibold">Vorschau</span>
          <span className="text-white/40 text-xs">{widthCm} x {heightCm} cm</span>
        </div>

        {/* Mode toggle: Vorschau / Mockup */}
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('preview')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              viewMode === 'preview'
                ? 'bg-white text-gray-900 shadow-lg'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            Vorschau
          </button>
          <button
            onClick={() => setViewMode('mockup')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
              viewMode === 'mockup'
                ? 'bg-white text-gray-900 shadow-lg'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <path d="M8 21h8M12 17v4"/>
            </svg>
            Mockup
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Scene selector — context-dependent */}
          {viewMode === 'preview' && (
            <div className="flex items-center gap-1">
              {SCENES.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setActiveScene(i)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    i === activeScene
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          <button
            onClick={() => {
              const printWin = window.open('', '_blank');
              if (printWin && imageUrl) {
                printWin.document.write(`<!DOCTYPE html><html><head><title>Selenny\u00AE Designer \u2013 Druckvorschau</title><style>@page{margin:10mm}body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#fff}img{max-width:100%;max-height:90vh;object-fit:contain}</style></head><body><img src="${imageUrl}" onload="setTimeout(()=>{window.print();window.close()},300)"/></body></html>`);
                printWin.document.close();
              }
            }}
            className="flex items-center gap-2 px-4 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Drucken
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
            Schließen
          </button>
        </div>
      </div>

      {/* Scene — Preview Mode */}
      {viewMode === 'preview' && (
        <div
          ref={containerRef}
          className="flex-1 overflow-hidden relative"
          style={{ background: scene.bg || '#222' }}
        >
          {/* Banner — centered with aspect ratio preserved */}
          {imageUrl && (
            <div
              className="absolute flex items-center justify-center"
              style={{
                top: scene.bannerPos.top,
                left: scene.bannerPos.left,
                right: scene.bannerPos.right,
                bottom: scene.bannerPos.bottom,
                zIndex: 2,
              }}
            >
              <div
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  aspectRatio: `${aspectRatio}`,
                  boxShadow: scene.shadow,
                  position: 'relative',
                  lineHeight: 0,
                }}
              >
                <img
                  src={imageUrl}
                  alt="Banner Vorschau"
                  style={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                />
                {/* Finishing overlay (Ösen, Saum, Hohlsaum) */}
                <FinishingOverlay
                  widthCm={widthCm}
                  heightCm={heightCm}
                  finishing={finishing}
                  hemPerSide={hemPerSide}
                  grommets={grommets}
                  hohlsaum={hohlsaum}
                />
                {/* Subtle light reflection */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(160deg, rgba(255,255,255,0.08) 0%, transparent 35%, transparent 65%, rgba(0,0,0,0.06) 100%)',
                    borderRadius: 1,
                    zIndex: 4,
                  }}
                />
              </div>
            </div>
          )}

          {/* Size reference label + finishing info */}
          <div className="absolute bottom-4 right-4 z-10 flex flex-col items-end gap-1.5">
            {finishingSummary && (
              <div className="bg-black/50 backdrop-blur-sm text-white/80 text-[11px] px-3 py-1.5 rounded-lg">
                {finishingSummary}
              </div>
            )}
            <div className="bg-black/50 backdrop-blur-sm text-white/80 text-[11px] px-3 py-1.5 rounded-lg">
              Originalgröße: {widthCm} x {heightCm} cm ({(widthCm * heightCm / 10000).toFixed(2)} m²)
            </div>
          </div>
        </div>
      )}

      {/* Scene — Mockup Mode */}
      {viewMode === 'mockup' && (
        <div className="flex-1 overflow-hidden relative flex flex-col">
          {/* Main mockup area */}
          <div className="flex-1 relative overflow-hidden" style={{ background: '#0a0a0a' }}>
            <MockupView
              imageUrl={imageUrl}
              scene={mockupScene}
              widthCm={widthCm}
              heightCm={heightCm}
              finishing={finishing}
              hemPerSide={hemPerSide}
              grommets={grommets}
              hohlsaum={hohlsaum}
            />
            {/* Size and finishing info */}
            <div className="absolute bottom-4 right-4 z-10 flex flex-col items-end gap-1.5">
              {finishingSummary && (
                <div className="bg-black/60 backdrop-blur-sm text-white/80 text-[11px] px-3 py-1.5 rounded-lg">
                  {finishingSummary}
                </div>
              )}
              <div className="bg-black/60 backdrop-blur-sm text-white/80 text-[11px] px-3 py-1.5 rounded-lg">
                Originalgröße: {widthCm} x {heightCm} cm ({(widthCm * heightCm / 10000).toFixed(2)} m²)
              </div>
            </div>
            {/* Scene label */}
            <div className="absolute top-4 left-4 z-10">
              <div className="bg-black/60 backdrop-blur-sm text-white/90 text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <path d="M8 21h8M12 17v4"/>
                </svg>
                {mockupScene.label}
              </div>
            </div>
          </div>

          {/* Mockup thumbnail selector */}
          <div className="bg-black/90 backdrop-blur-sm px-4 py-3 flex items-center justify-center gap-3">
            {MOCKUP_SCENES.map((ms, i) => (
              <button
                key={ms.id}
                onClick={() => setActiveMockup(i)}
                className={`relative rounded-lg overflow-hidden transition-all ${
                  i === activeMockup
                    ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-black scale-105'
                    : 'opacity-60 hover:opacity-90 hover:scale-102'
                }`}
                style={{ width: 120, height: 80 }}
              >
                <img
                  src={ms.src}
                  alt={ms.label}
                  className="w-full h-full object-cover"
                />
                {/* Mini design preview overlay on thumbnail */}
                {imageUrl && (
                  <div
                    className="absolute"
                    style={{
                      top: ms.placement.top,
                      left: ms.placement.left,
                      width: ms.placement.width,
                      height: ms.placement.height,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt=""
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        opacity: 0.9,
                      }}
                    />
                  </div>
                )}
                <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[9px] font-medium text-center py-0.5">
                  {ms.label}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom bar */}
      <div className="flex items-center justify-center gap-4 px-5 py-2.5 bg-black/80 backdrop-blur-sm">
        <span className="text-white/40 text-[11px]">
          <span className="inline-block w-4 h-4 align-middle mr-1">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full"><path d="M15 18l-6-6 6-6"/></svg>
          </span>
          <span className="inline-block w-4 h-4 align-middle mr-1">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full"><path d="M9 18l6-6-6-6"/></svg>
          </span>
          {viewMode === 'mockup' ? 'Mockup wechseln' : 'Szene wechseln'}
        </span>
        <span className="text-white/20">|</span>
        <span className="text-white/40 text-[11px]">ESC zum Schließen</span>
      </div>
    </div>
  );
}

export default PreviewOverlay;
